import { useState } from "react";
import {
  BookmarkCheck,
  Check,
  ChevronRight,
  Clock3,
  Database,
  Download,
  RefreshCw,
  Trash2,
  WifiOff,
  X,
} from "lucide-react";
import { formatDateTime } from "../format";
import type {
  DataSnapshot,
  EvaluatedToilet,
  SavedRegionPack,
  UserNeeds,
} from "../types";
import { useModalDialog } from "../useModalDialog";

export function OfflinePanel({
  snapshot,
  saved,
  online,
  pendingReports,
  onSave,
  onDelete,
  onRefresh,
  onClose,
  needs,
  favoriteItems = [],
  onEditNeeds,
  onSelectFavorite,
  onResetPreferences,
}: {
  snapshot: DataSnapshot;
  saved?: SavedRegionPack;
  online: boolean;
  pendingReports: number;
  onSave: () => Promise<void>;
  onDelete: () => Promise<void>;
  onRefresh: () => Promise<void>;
  onClose: () => void;
  needs?: UserNeeds;
  favoriteItems?: EvaluatedToilet[];
  onEditNeeds?: () => void;
  onSelectFavorite?: (item: EvaluatedToilet) => void;
  onResetPreferences?: () => Promise<void>;
}) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [activeAction, setActiveAction] = useState<
    "save" | "refresh" | "delete" | "preferences"
  >();
  const [actionMessage, setActionMessage] = useState<string>();
  const [actionError, setActionError] = useState<string>();
  const dialogRef = useModalDialog<HTMLElement>(onClose);
  const savedNeedLabels = needs
    ? [
        needs.wheelchair ? "車いす" : undefined,
        needs.ostomate ? "オストメイト" : undefined,
        needs.baby_changing ? "おむつ交換" : undefined,
        needs.openNow ? "営業中" : undefined,
      ].filter((label): label is string => Boolean(label))
    : [];
  const runAction = async (
    action: "save" | "refresh" | "delete" | "preferences",
    callback: () => Promise<void>,
    successMessage: string,
  ) => {
    if (activeAction) return;
    setActiveAction(action);
    setActionMessage(undefined);
    setActionError(undefined);
    try {
      await callback();
      setActionMessage(successMessage);
    } catch {
      setActionError(
        "操作を完了できませんでした。端末の空き容量と通信状態を確認して、もう一度お試しください。",
      );
    } finally {
      setActiveAction(undefined);
    }
  };

  return (
    <div className="dialog-backdrop" onMouseDown={onClose}>
      <section
        ref={dialogRef}
        className="offline-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="offline-title"
        tabIndex={-1}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="drawer-heading">
          <div>
            <p className="dialog-step">この端末の準備</p>
            <h2 id="offline-title">保存・オフライン</h2>
          </div>
          <button
            className="icon-button"
            type="button"
            onClick={onClose}
            data-dialog-initial-focus
          >
            <X aria-hidden="true" />
            <span className="sr-only">保存・オフライン画面を閉じる</span>
          </button>
        </div>

        {needs ? (
          <section className="saved-preferences" aria-labelledby="saved-needs-title">
            <div>
              <BookmarkCheck aria-hidden="true" />
              <span>
                <h3 id="saved-needs-title">次回も使う条件</h3>
                <p>
                  {savedNeedLabels.length > 0
                    ? savedNeedLabels.join("・")
                    : "設備条件なし"}
                </p>
              </span>
              {onEditNeeds ? (
                <button type="button" onClick={onEditNeeds}>
                  変更
                </button>
              ) : null}
            </div>
            {favoriteItems.length > 0 ? (
              <div className="saved-favorites">
                <h3>保存した候補</h3>
                {favoriteItems.slice(0, 4).map((item) => (
                  <button
                    type="button"
                    key={item.id}
                    onClick={() => onSelectFavorite?.(item)}
                  >
                    <span>
                      <b>{item.name}</b>
                      <small>徒歩目安 {item.walkingMinutes}分</small>
                    </span>
                    <ChevronRight aria-hidden="true" />
                  </button>
                ))}
              </div>
            ) : null}
            <p className="saved-preferences__privacy">
              条件と候補はこの端末だけに保存し、位置や利用履歴は保存しません。
            </p>
            {onResetPreferences ? (
              <button
                className="saved-preferences__reset"
                type="button"
                disabled={Boolean(activeAction)}
                onClick={() =>
                  void runAction(
                    "preferences",
                    onResetPreferences,
                    "この端末の条件と保存候補を消去しました。",
                  )
                }
              >
                <Trash2 aria-hidden="true" />
                {activeAction === "preferences"
                  ? "消去中…"
                  : "条件・保存候補を消去"}
              </button>
            ) : null}
          </section>
        ) : null}

        {!online && (
          <div className="offline-alert" role="status">
            <WifiOff aria-hidden="true" />
            <span>
              <b>オフラインです</b>
              動的な利用確認は「保存時点では利用可能」として表示します。
            </span>
          </div>
        )}

        <dl className="offline-facts">
          <div>
            <dt>
              <Database aria-hidden="true" />
              保存地域
            </dt>
            <dd>{snapshot.region.name}・周辺{snapshot.region.radiusMeters / 1000}km</dd>
          </div>
          <div>
            <dt>
              <Clock3 aria-hidden="true" />
              最終保存
            </dt>
            <dd>{saved ? formatDateTime(saved.savedAt) : "未保存"}</dd>
          </div>
          <div>
            <dt>
              <Download aria-hidden="true" />
              保存対象
            </dt>
            <dd>
              トイレ{snapshot.toilets.length}施設・出典{snapshot.sources.length}件
            </dd>
          </div>
          <div>
            <dt>
              <RefreshCw aria-hidden="true" />
              保留中の報告
            </dt>
            <dd>{pendingReports}件</dd>
          </div>
        </dl>

        <p className="offline-limitation">
          <Check aria-hidden="true" />
          地図タイルは提供者の規約に従い一括保存しません。地図がなくても、住所・距離・設備・判断根拠を確認できます。
        </p>

        {actionMessage && (
          <p className="offline-action-message" role="status">
            {actionMessage}
          </p>
        )}
        {actionError && (
          <p className="offline-action-error" role="alert">
            {actionError}
          </p>
        )}

        <div className="offline-actions">
          {saved ? (
            <>
              <button
                className="primary-button"
                type="button"
                onClick={() =>
                  void runAction(
                    "refresh",
                    onRefresh,
                    "この地域の保存情報を更新しました。",
                  )
                }
                disabled={!online || Boolean(activeAction)}
              >
                <RefreshCw aria-hidden="true" />
                {activeAction === "refresh" ? "更新中…" : "この地域を更新"}
              </button>
              {confirmDelete ? (
                <div className="delete-confirmation" role="alert">
                  <p>
                    <b>この地域の保存データを削除しますか？</b>
                    保留中の利用報告は削除されません。
                  </p>
                  <div>
                    <button
                      className="secondary-button"
                      type="button"
                      disabled={Boolean(activeAction)}
                      onClick={() => setConfirmDelete(false)}
                    >
                      キャンセル
                    </button>
                    <button
                      className="danger-button"
                      type="button"
                      disabled={Boolean(activeAction)}
                      onClick={() =>
                        void runAction(
                          "delete",
                          async () => {
                            await onDelete();
                            setConfirmDelete(false);
                          },
                          "この地域の保存情報を削除しました。",
                        )
                      }
                    >
                      <Trash2 aria-hidden="true" />
                      {activeAction === "delete" ? "削除中…" : "削除する"}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  className="danger-button"
                  type="button"
                  disabled={Boolean(activeAction)}
                  onClick={() => setConfirmDelete(true)}
                >
                  <Trash2 aria-hidden="true" />
                  保存を削除
                </button>
              )}
            </>
          ) : (
            <button
              className="primary-button"
              type="button"
              disabled={Boolean(activeAction)}
              onClick={() =>
                void runAction(
                  "save",
                  onSave,
                  "この地域をオフライン用に保存しました。",
                )
              }
            >
              <Download aria-hidden="true" />
              {activeAction === "save" ? "保存中…" : "この地域を保存"}
            </button>
          )}
        </div>
      </section>
    </div>
  );
}
